import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Sparkles, Heart } from 'lucide-react';

const NicknameFlipCards: React.FC = () => {
    const { getArray } = useLanguage();
    const nicknames = getArray('nicknames');
    const [flippedCards, setFlippedCards] = useState<Set<number>>(new Set());

    const toggleFlip = (id: number) => {
        const newFlipped = new Set(flippedCards);
        if (newFlipped.has(id)) {
            newFlipped.delete(id);
        } else {
            newFlipped.add(id);
        }
        setFlippedCards(newFlipped);
    };

    return (
        <div className="px-6 py-12 max-w-6xl mx-auto">
            <div className="text-center mb-10">
                <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                    className="inline-block text-primary-red mb-3"
                >
                    <Sparkles size={28} />
                </motion.div>
                <h2 className="text-2xl md:text-3xl font-playfair font-bold text-primary-red mb-3">Special Names For You</h2>
                <p className="text-gray-500 dark:text-gray-400 font-poppins text-sm">Tap each card to reveal what I call you in my heart</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
                {nicknames.map((nickname) => (
                    <div
                        key={nickname.id}
                        className="perspective-1000 h-32 md:h-40 group cursor-pointer"
                        onClick={() => toggleFlip(nickname.id)}
                    >
                        <motion.div
                            animate={{ rotateY: flippedCards.has(nickname.id) ? 180 : 0 }}
                            transition={{ duration: 0.6, type: "spring", stiffness: 260, damping: 20 }}
                            className="relative w-full h-full preserve-3d shadow-md rounded-2xl md:rounded-3xl"
                        >
                            {/* Front Side */}
                            <div className="absolute inset-0 backface-hidden bg-primary-red flex items-center justify-center rounded-2xl md:rounded-3xl p-4 text-white z-20">
                                <div className="flex flex-col items-center">
                                    <Heart size={24} className="mb-2 opacity-50" />
                                    <span className="text-sm font-bold font-poppins tracking-widest">REVEAL</span>
                                </div>
                            </div>

                            {/* Back Side */}
                            <div
                                className="absolute inset-0 backface-hidden bg-white dark:bg-romantic-cardDark flex items-center justify-center rounded-2xl md:rounded-3xl p-4 border-2 border-primary-red/20 rotateY-180 z-10"
                            >
                                <div className="flex flex-col items-center text-center">
                                    <h3 className="text-lg md:text-xl font-playfair font-bold text-primary-red leading-tight">
                                        {nickname.name || nickname.text}
                                    </h3>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                ))}
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                className="mt-16 p-6 rounded-[2rem] bg-romantic-pink/5 dark:bg-primary-red/5 text-center border border-primary-red/5"
            >
                <p className="text-gray-500 dark:text-gray-400 font-poppins text-sm leading-relaxed">
                    Every name holds a different memory, a different smile, and the same infinite love I have for you. ❤️
                </p>
            </motion.div>
        </div>
    );
};

export default NicknameFlipCards;
